import React, { useEffect, useRef, useState } from 'react';
import axios from '../api/axiosInstance';
import { getUserRoles } from '../auth/cookieUtils';

const COMPANY_STORAGE_KEY = 'selectedCompanyId';
const LOCATION_STORAGE_KEY = 'selectedLocationId';
const BOILER_MODULE_STORAGE_KEY = 'selectedLocationBoilerModuleEnabled';

const publishLocationContext = (location) => {
  if (location?.id) {
    sessionStorage.setItem(
      BOILER_MODULE_STORAGE_KEY,
      String(Boolean(location.boilerModuleEnabled))
    );
  } else {
    sessionStorage.removeItem(BOILER_MODULE_STORAGE_KEY);
  }

  window.dispatchEvent(new CustomEvent('odin:location-context-changed', {
    detail: {
      locationId: location?.id ?? null,
      boilerModuleEnabled: Boolean(location?.boilerModuleEnabled)
    }
  }));
};

const CompanyLocationSelector = ({ onCompanyChange, onLocationChange }) => {
  const [companies, setCompanies] = useState([]);
  const [locations, setLocations] = useState([]);

  const [selectedCompanyId, setSelectedCompanyId] = useState(() => {
  const saved = sessionStorage.getItem(COMPANY_STORAGE_KEY);
  return saved ? Number(saved) : null;
});

const [selectedLocationId, setSelectedLocationId] = useState(() => {
  const saved = sessionStorage.getItem(LOCATION_STORAGE_KEY);
  return saved ? Number(saved) : null;
});
  const onCompanyChangeRef = useRef(onCompanyChange);
  const onLocationChangeRef = useRef(onLocationChange);

  useEffect(() => {
    onCompanyChangeRef.current = onCompanyChange;
    onLocationChangeRef.current = onLocationChange;
  }, [onCompanyChange, onLocationChange]);

  // Extract user roles from cookies
  const roles = getUserRoles();
  const isAdmin = roles.includes('ADMIN') || roles.includes('SUPER_ADMIN');

  useEffect(() => {
    let cancelled = false;
    const fetchCompanies = async () => {
      try {
        const endpoint = isAdmin
          ? '/api/companies/options' // Admins fetch all companies
          : '/api/companies/options/available-to-user'; // Normal users fetch restricted companies

        const response = await axios.get(endpoint);
        if (cancelled) return;
        const data = Array.isArray(response.data) ? response.data : [];
        setCompanies(data);

        const savedCompanyId = Number(sessionStorage.getItem(COMPANY_STORAGE_KEY)) || null;

        const savedCompanyStillExists = data.some(
          (company) => company.id === savedCompanyId
        );

        if (savedCompanyStillExists) {
          setSelectedCompanyId(savedCompanyId);
          onCompanyChangeRef.current?.(savedCompanyId);
        } else if (data.length > 0) {
          const initialCompanyId = data[0].id;
          setSelectedCompanyId(initialCompanyId);
          sessionStorage.setItem(COMPANY_STORAGE_KEY, String(initialCompanyId));
          onCompanyChangeRef.current?.(initialCompanyId);
        } else {
          setSelectedCompanyId(null);
          sessionStorage.removeItem(COMPANY_STORAGE_KEY);
          sessionStorage.removeItem(LOCATION_STORAGE_KEY);
          publishLocationContext(null);
          onCompanyChangeRef.current?.(null);
          onLocationChangeRef.current?.(null);
        }
      } catch (error) {
        if (cancelled) return;
        console.error('Error fetching companies:', error);
        setCompanies([]);
      }
    };
    fetchCompanies();
    return () => { cancelled = true; };
  }, [isAdmin]);

  useEffect(() => {
  let cancelled = false;
  const fetchLocationsByCompany = async () => {
    if (!selectedCompanyId) {
      setLocations([]);
      setSelectedLocationId(null);
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
      publishLocationContext(null);
      onLocationChangeRef.current?.(null);
      return;
    }

    try {
      const endpoint = isAdmin
        ? `/api/locations/options/by-company/${selectedCompanyId}`
        : `/api/locations/options/by-company/${selectedCompanyId}/available-to-user`;

      const response = await axios.get(endpoint);
      if (cancelled) return;
      const data = Array.isArray(response.data) ? response.data : [];
      setLocations(data);

      const savedLocationId = Number(sessionStorage.getItem(LOCATION_STORAGE_KEY)) || null;
      const savedLocation = data.find(
        (loc) => loc.id === savedLocationId
      );

      if (savedLocation) {
        setSelectedLocationId(savedLocationId);
        publishLocationContext(savedLocation);
        onLocationChangeRef.current?.(savedLocationId);
      } else {
        const initialLocation = data.length > 0 ? data[0] : null;
        const initialLocationId = initialLocation?.id ?? null;
        setSelectedLocationId(initialLocationId);

        if (initialLocationId !== null) {
          sessionStorage.setItem(LOCATION_STORAGE_KEY, String(initialLocationId));
        } else {
          sessionStorage.removeItem(LOCATION_STORAGE_KEY);
        }

        publishLocationContext(initialLocation);
        onLocationChangeRef.current?.(initialLocationId);
      }
    } catch (error) {
      if (cancelled) return;
      console.error('Error fetching locations by company:', error);
      setLocations([]);
      setSelectedLocationId(null);
      sessionStorage.removeItem(LOCATION_STORAGE_KEY);
      publishLocationContext(null);
      onLocationChangeRef.current?.(null);
    }
  };

  fetchLocationsByCompany();
  return () => { cancelled = true; };
}, [selectedCompanyId, isAdmin]);

  const handleCompanyChange = (e) => {
  const companyId = Number(e.target.value);

  setSelectedCompanyId(companyId);
  setSelectedLocationId(null);

  sessionStorage.setItem(COMPANY_STORAGE_KEY, String(companyId));
  sessionStorage.removeItem(LOCATION_STORAGE_KEY);
  publishLocationContext(null);

  onCompanyChangeRef.current?.(companyId);
  onLocationChangeRef.current?.(null);
};

  const handleLocationChange = (e) => {
  const locationId = Number(e.target.value);

  setSelectedLocationId(locationId);
  sessionStorage.setItem(LOCATION_STORAGE_KEY, String(locationId));
  publishLocationContext(locations.find((location) => location.id === locationId) || null);

  onLocationChangeRef.current?.(locationId);
};

  return (
    <>
      <select
        className="company-select"
        value={selectedCompanyId || ''}
        onChange={handleCompanyChange}
      >
        <option value="" disabled>Select Company</option>
        {companies.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
      <select
        className="location-select"
        value={selectedLocationId || ''}
        onChange={handleLocationChange}
        disabled={!locations.length}
      >
        <option value="" disabled>Select Location</option>
        {locations.map((l) => (
          <option key={l.id} value={l.id}>
            {l.name}
          </option>
        ))}
      </select>
    </>
  );
};

export default CompanyLocationSelector;
